import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  FormControlLabel,
  Grid,
  Switch,
  Checkbox,
  Stack,
  MenuItem,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import api from "../../services/api";
import useToastStore from "../../store/toastStore";
import UserTable from "../../components/admin/UserTable";
import UserDetailModal from "../../components/admin/UserDetailModal";
import LoadingSkeleton from "../../components/common/LoadingSkeleton";
import EmptyState from "../../components/admin/EmptyState";
import { ECOPICK_ROLE_LIST } from "../../constants";
import koreaRegions from "../../data/koreaRegions.json";

const dreamRoleLabels = {
  dream_user: "이용자",
  dream_collector: "작업자",
};

const accountStatusLabels = {
  APPROVED: "정상",
  PENDING_APPROVAL: "승인 대기",
  REJECTED: "반려",
  SUSPENDED: "정지",
  WITHDRAWN: "탈퇴",
};

const requestStatusLabels = {
  PENDING: "대기",
  ASSIGNED: "배정",
  ON_THE_WAY: "이동 중",
  AWAITING_APPROVAL: "검수 대기",
  COMPLETED: "완료",
  CANCELLED: "취소",
  REJECTED: "거절",
};

const formatDateTime = (value) => value ? new Date(value).toLocaleString("ko-KR") : "-";
const formatPrice = (value) => `${Number(value || 0).toLocaleString("ko-KR")}원`;

function requestLocation(row) {
  return row?.location || row || {};
}

function requestItemsText(location) {
  if (location.itemsText) return location.itemsText;
  if (!Array.isArray(location.items) || location.items.length === 0) return "-";
  return location.items
    .map((item) => `${item.itemName || item.name || "품목"} x${item.quantity ?? 0}`)
    .join(", ");
}

function requestTotalPrice(location) {
  if (location.totalPrice != null) return location.totalPrice;
  if (location.estimatedTotalPrice != null) return location.estimatedTotalPrice;
  if (!Array.isArray(location.items)) return 0;
  return location.items.reduce((sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 0), 0);
}

function DreamUserDetailDialog({
  open,
  onClose,
  user,
  loading,
  requests,
  requestsLoading,
  selectedRole,
  onManageStatus,
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>들어드림 {dreamRoleLabels[selectedRole] || "계정"} 상세</DialogTitle>
      <DialogContent>
        {loading ? (
          <LoadingSkeleton variant="table" rows={4} />
        ) : !user ? (
          <EmptyState message="계정 상세를 불러올 수 없습니다." />
        ) : (
          <Stack spacing={3} sx={{ pt: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{user.name || "-"}</Typography>
                <Typography variant="body2" color="text.secondary">{user.email || "-"}</Typography>
                <Typography variant="body2" color="text.secondary">{user.phoneNumber || "-"}</Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  size="small"
                  color={user.accountStatus === "APPROVED" ? "success" : user.accountStatus === "PENDING_APPROVAL" ? "warning" : "default"}
                  label={accountStatusLabels[user.accountStatus] || user.accountStatus || "-"}
                />
                {user.phoneVerified != null && (
                  <Chip size="small" color={user.phoneVerified ? "success" : "error"} variant="outlined" label={user.phoneVerified ? "휴대폰 인증" : "휴대폰 미인증"} />
                )}
                {onManageStatus && selectedRole !== "dream_collector" && (
                  <Button size="small" variant="outlined" onClick={() => onManageStatus(user)}>
                    상태관리
                  </Button>
                )}
              </Stack>
            </Box>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 3 }}>
                <Typography variant="caption" color="text.secondary">가입일</Typography>
                <Typography>{formatDateTime(user.createdAt)}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Typography variant="caption" color="text.secondary">요청/작업 수</Typography>
                <Typography>{user.dreamRequestCount ?? 0}건</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Typography variant="caption" color="text.secondary">최근 요청</Typography>
                <Typography>{formatDateTime(user.dreamLastRequestAt)}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Typography variant="caption" color="text.secondary">소셜</Typography>
                <Typography>{user.socialProvider || "-"}</Typography>
              </Grid>
            </Grid>

            {selectedRole === "dream_user" ? (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>기본 주소</Typography>
                <Typography>{user.defaultAddress || "-"}</Typography>
                {user.detailAddress && <Typography color="text.secondary">{user.detailAddress}</Typography>}
              </Box>
            ) : (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>사업자 정보</Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 4 }}><Typography variant="body2">상호명: {user.businessName || "-"}</Typography></Grid>
                  <Grid size={{ xs: 12, md: 4 }}><Typography variant="body2">사업자번호: {user.businessNumber || "-"}</Typography></Grid>
                  <Grid size={{ xs: 12, md: 4 }}><Typography variant="body2">대표자명: {user.representativeName || "-"}</Typography></Grid>
                  <Grid size={{ xs: 12, md: 4 }}><Typography variant="body2">차량: {user.hasVehicle ? "보유" : "미보유"}</Typography></Grid>
                </Grid>
              </Box>
            )}

            <Divider />

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                {selectedRole === "dream_collector" ? "작업 내역" : "요청 내역"}
              </Typography>
              {requestsLoading ? (
                <LoadingSkeleton variant="table" rows={5} />
              ) : !requests?.length ? (
                <EmptyState message={selectedRole === "dream_collector" ? "작업 내역이 없습니다." : "요청 내역이 없습니다."} />
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>요청</TableCell>
                        <TableCell>상태</TableCell>
                        <TableCell>일정</TableCell>
                        <TableCell>지역/주소</TableCell>
                        <TableCell>{selectedRole === "dream_collector" ? "이용자" : "작업자"}</TableCell>
                        <TableCell>품목</TableCell>
                        <TableCell align="right">금액</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {requests.map((row) => {
                        const location = requestLocation(row);
                        return (
                          <TableRow key={location.marketLocationId || row.marketLocationId} hover>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>#{location.marketLocationId || row.marketLocationId}</Typography>
                              {row.overdue && <Chip size="small" color="error" variant="outlined" label="기한 경과" />}
                              {row.adminVoided && <Chip size="small" color="error" label="무효" sx={{ ml: row.overdue ? 0.5 : 0 }} />}
                            </TableCell>
                            <TableCell>
                              <Chip size="small" label={requestStatusLabels[location.status] || location.status || "-"} />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{location.desiredDate || "-"}</Typography>
                              <Typography variant="caption" color="text.secondary">{location.desiredTimeSlot || "-"}</Typography>
                            </TableCell>
                            <TableCell sx={{ maxWidth: 280 }}>
                              <Typography variant="body2">{`${location.regionSido || "-"} ${location.regionSigungu || ""}`}</Typography>
                              <Tooltip title={`${location.address || ""} ${location.detailAddress || ""}`.trim()}>
                                <Typography variant="caption" color="text.secondary" noWrap>{location.address || "-"}</Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell>{selectedRole === "dream_collector" ? (location.userName || "-") : (location.collectorName || "미배정")}</TableCell>
                            <TableCell sx={{ maxWidth: 260 }}>
                              <Tooltip title={requestItemsText(location)}>
                                <Typography variant="body2" noWrap>{requestItemsText(location)}</Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="right">{formatPrice(requestTotalPrice(location))}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>닫기</Button>
      </DialogActions>
    </Dialog>
  );
}

const defaultListEndpointBuilder = (role) => `/api/users/role/${role}`;
const defaultDetailEndpointBuilder = (userId) => `/api/dashboard/metrics/user/${userId}`;

function UserManagementPage({
  title = "사용자 관리",
  roles = ECOPICK_ROLE_LIST,
  defaultRole = "user",
  enableCollectorApproval = false,
  readOnly = false,
  listEndpointBuilder = defaultListEndpointBuilder,
  detailEndpointBuilder = defaultDetailEndpointBuilder,
  requestHistoryEndpointBuilder = null,
  deleteEndpoint = "/api/users",
  enableAccountStatusManagement = false,
  accountStatusEndpointBuilder = (userId) => `/api/admin/dream-users/${userId}/status`,
  deleteDialogTitle = "사용자 삭제 확인",
  deleteDialogDescription,
  deleteDialogWarning = "이 작업은 되돌릴 수 없습니다.",
  deleteSuccessMessage,
}) {
  const canMutate = !readOnly;
  const canOpenDetail = Boolean(detailEndpointBuilder);
  const canDelete = canMutate && Boolean(deleteEndpoint);
  const [selectedRole, setSelectedRole] = useState(defaultRole);
  const [users, setUsers] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [search, setSearch] = useState("");

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUserData, setSelectedUserData] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [requestHistory, setRequestHistory] = useState([]);
  const [requestHistoryLoading, setRequestHistoryLoading] = useState(false);

  // Bulk delete state
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [approvalTarget, setApprovalTarget] = useState(null);
  const [approvalStatus, setApprovalStatus] = useState("APPROVED");
  const [approvalReason, setApprovalReason] = useState("");
  const [accountStatusTarget, setAccountStatusTarget] = useState(null);
  const [accountStatus, setAccountStatus] = useState("APPROVED");
  const [accountStatusReason, setAccountStatusReason] = useState("");
  const [accountStatusAnonymize, setAccountStatusAnonymize] = useState(false);
  const [hasVehicle, setHasVehicle] = useState(false);
  const [approvalRegions, setApprovalRegions] = useState([]);
  const [regionSido, setRegionSido] = useState(koreaRegions[0]?.sido || "");
  const [regionSigungu, setRegionSigungu] = useState(koreaRegions[0]?.sigungu?.[0] || "");

  const showToast = useToastStore((s) => s.showToast);

  const fetchUsers = useCallback(async () => {
    setListLoading(true);
    try {
      const response = await api.get(listEndpointBuilder(selectedRole));
      setUsers(response.data);
    } catch (error) {
      console.error("사용자 목록을 불러오는 데 실패했습니다.", error);
      setUsers([]);
    } finally {
      setListLoading(false);
    }
  }, [selectedRole, listEndpointBuilder]);

  useEffect(() => {
    fetchUsers();
    setSelectedUserIds([]);
    setSearch("");
  }, [fetchUsers]);

  const handleTabChange = (event, newValue) => {
    setSelectedRole(newValue);
  };

  // Filter users by search
  const filteredUsers = useMemo(() => {
    if (!search) return users;
    const q = search.toLowerCase();
    return users.filter(
      (user) =>
        (user.name || "").toLowerCase().includes(q) ||
        (user.email || "").toLowerCase().includes(q) ||
        (user.phoneNumber || "").toLowerCase().includes(q)
    );
  }, [users, search]);

  // User click -> open detail modal
  const handleUserClick = async (userId) => {
    if (!detailEndpointBuilder) return;
    setIsModalOpen(true);
    setModalLoading(true);
    setRequestHistoryLoading(Boolean(requestHistoryEndpointBuilder));
    setRequestHistory([]);
    try {
      const [detailResponse, historyResponse] = await Promise.all([
        api.get(detailEndpointBuilder(userId)),
        requestHistoryEndpointBuilder
          ? api.get(requestHistoryEndpointBuilder(userId))
          : Promise.resolve({ data: [] }),
      ]);
      setSelectedUserData(detailResponse.data);
      setRequestHistory(historyResponse.data || []);
    } catch (error) {
      console.error("상세 데이터를 불러오는 데 실패했습니다.", error);
      setSelectedUserData(null);
      setRequestHistory([]);
    } finally {
      setModalLoading(false);
      setRequestHistoryLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedUserData(null);
    setRequestHistory([]);
    setRequestHistoryLoading(false);
  };

  // Selection handlers
  const handleSelectionChange = (ids) => {
    setSelectedUserIds(ids);
  };

  // Bulk delete
  const handleBulkDelete = async () => {
    try {
      await api.delete(deleteEndpoint, { data: selectedUserIds });
      showToast(deleteSuccessMessage || `${selectedUserIds.length}명의 사용자가 삭제되었습니다.`, "success");
      setSelectedUserIds([]);
      setDeleteDialogOpen(false);
      fetchUsers();
    } catch (err) {
      showToast(err?.response?.data?.message || "삭제에 실패했습니다.", "error");
    }
  };

  const openApprovalDialog = async (user) => {
    setApprovalTarget(user);
    setApprovalStatus(user.accountStatus || "APPROVED");
    setApprovalReason("");
    setHasVehicle(Boolean(user.hasVehicle));
    setApprovalRegions([]);
    try {
      const response = await api.get(`/api/admin/collectors/${user.userId}/service-regions`);
      setApprovalRegions(response.data || []);
    } catch (err) {
      console.warn("작업자 활동 지역 조회 실패:", err);
      showToast("활동 지역 조회에 실패했습니다. 저장 전 확인해 주세요.", "warning");
    }
  };

  const handleApprovalSave = async () => {
    if (!approvalTarget) return;
    if (!approvalReason.trim()) {
      showToast("작업자 승인/활동 지역 변경 사유를 입력해 주세요.", "warning");
      return;
    }
    try {
      await api.patch(`/api/admin/collectors/${approvalTarget.userId}/approval`, {
        accountStatus: approvalStatus,
        reason: approvalReason,
        collectorRejectionReason: approvalStatus === "REJECTED" ? approvalReason : undefined,
        hasVehicle,
        serviceRegions: approvalRegions,
      });
      showToast("작업자 승인 정보가 저장되었습니다.", "success");
      setApprovalTarget(null);
      fetchUsers();
    } catch (err) {
      console.error("작업자 승인 저장 실패:", err);
      showToast("작업자 승인 정보 저장에 실패했습니다.", "error");
    }
  };

  const openAccountStatusDialog = (user) => {
    setAccountStatusTarget(user);
    setAccountStatus(user.accountStatus || "APPROVED");
    setAccountStatusReason("");
    setAccountStatusAnonymize(false);
  };

  const handleAccountStatusSave = async () => {
    if (!accountStatusTarget) return;
    if (!accountStatusReason.trim()) {
      showToast("계정 상태 변경 사유를 입력해 주세요.", "warning");
      return;
    }
    try {
      await api.patch(accountStatusEndpointBuilder(accountStatusTarget.userId), {
        accountStatus,
        reason: accountStatusReason,
        anonymizePersonalData: accountStatus === "WITHDRAWN" && accountStatusAnonymize,
      });
      showToast("계정 상태가 저장되었습니다.", "success");
      setAccountStatusTarget(null);
      fetchUsers();
    } catch (err) {
      console.error("계정 상태 저장 실패:", err);
      showToast(err?.response?.data?.message || "계정 상태 저장에 실패했습니다.", "error");
    }
  };

  const currentSigunguOptions =
    koreaRegions.find((region) => region.sido === regionSido)?.sigungu || [];

  const addApprovalRegion = () => {
    if (!regionSido || !regionSigungu) return;
    const next = { sido: regionSido, sigungu: regionSigungu };
    setApprovalRegions((prev) => {
      if (prev.some((region) => region.sido === next.sido && region.sigungu === next.sigungu)) {
        return prev;
      }
      return [...prev, next];
    });
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4">{title}</Typography>
        {canDelete && selectedUserIds.length > 0 && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => setDeleteDialogOpen(true)}
          >
            선택 삭제 ({selectedUserIds.length})
          </Button>
        )}
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={selectedRole}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          {roles.map((role) => (
            <Tab key={role.value} label={role.label} value={role.value} />
          ))}
        </Tabs>
      </Box>

      <TextField
        size="small"
        placeholder="이름, 이메일, 전화번호 검색..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2, width: 360 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
        }}
      />

      {search && (
        <Chip
          label={`${filteredUsers.length}명 검색됨`}
          size="small"
          sx={{ ml: 1, mb: 2 }}
        />
      )}

      <Box sx={{ paddingTop: 1 }}>
        {listLoading ? (
          <LoadingSkeleton variant="table" rows={8} />
        ) : (
          <UserTable
            users={filteredUsers}
            onUserClick={canMutate && canOpenDetail ? handleUserClick : null}
            selectable={canDelete}
            selectedIds={selectedUserIds}
            onSelectionChange={handleSelectionChange}
            onCollectorApproval={canMutate && enableCollectorApproval && selectedRole === "dream_collector" ? openApprovalDialog : null}
            onAccountStatusManage={canMutate && enableAccountStatusManagement && selectedRole !== "dream_collector" ? openAccountStatusDialog : null}
          />
        )}
      </Box>

      {requestHistoryEndpointBuilder ? (
        <DreamUserDetailDialog
          open={isModalOpen}
          onClose={handleCloseModal}
          user={selectedUserData}
          loading={modalLoading}
          requests={requestHistory}
          requestsLoading={requestHistoryLoading}
          selectedRole={selectedRole}
          onManageStatus={canMutate && enableAccountStatusManagement ? (targetUser) => {
            handleCloseModal();
            openAccountStatusDialog(targetUser);
          } : null}
        />
      ) : (
        <UserDetailModal
          open={isModalOpen}
          onClose={handleCloseModal}
          userData={selectedUserData}
          loading={modalLoading}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>{deleteDialogTitle}</DialogTitle>
        <DialogContent>
          <Typography>
            {deleteDialogDescription || `선택한 ${selectedUserIds.length}명의 사용자를 정말 삭제하시겠습니까?`}
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {deleteDialogWarning}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>취소</Button>
          <Button variant="contained" color="error" onClick={handleBulkDelete}>
            삭제
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!approvalTarget} onClose={() => setApprovalTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>작업자 승인 관리</DialogTitle>
        <DialogContent>
          {approvalTarget && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body2">
                {approvalTarget.name} / {approvalTarget.phoneNumber || "-"}
              </Typography>
              <TextField
                select
                label="승인 상태"
                value={approvalStatus}
                onChange={(e) => setApprovalStatus(e.target.value)}
                SelectProps={{ native: true }}
              >
                <option value="PENDING_APPROVAL">승인 대기</option>
                <option value="APPROVED">승인</option>
                <option value="REJECTED">반려</option>
                <option value="SUSPENDED">정지</option>
                <option value="WITHDRAWN">탈퇴</option>
              </TextField>
              <FormControlLabel
                control={<Switch checked={hasVehicle} onChange={(e) => setHasVehicle(e.target.checked)} />}
                label="차량 보유"
              />
              <TextField
                label="변경 사유"
                value={approvalReason}
                onChange={(e) => setApprovalReason(e.target.value)}
                multiline
                minRows={2}
                inputProps={{ maxLength: 500 }}
                helperText="승인, 반려, 정지, 복구, 활동 지역 변경 사유가 처리 이력에 저장됩니다."
              />
              <Box sx={{ p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>사업자 정보</Typography>
                <Typography variant="body2">사업자번호: {approvalTarget.businessNumber || "-"}</Typography>
                <Typography variant="body2">상호명: {approvalTarget.businessName || "-"}</Typography>
                <Typography variant="body2">대표자명: {approvalTarget.representativeName || "-"}</Typography>
                {approvalTarget.businessLicenseImageUrl ? (
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    <Box
                      component="img"
                      src={approvalTarget.businessLicenseImageUrl}
                      alt="사업자등록증"
                      sx={{ width: "100%", maxHeight: 220, objectFit: "contain", bgcolor: "#f5f5f5", borderRadius: 1 }}
                    />
                    <Button
                      size="small"
                      startIcon={<OpenInNewIcon />}
                      onClick={() => window.open(approvalTarget.businessLicenseImageUrl, "_blank", "noopener,noreferrer")}
                    >
                      새 창에서 보기
                    </Button>
                  </Stack>
                ) : (
                  <Typography variant="caption" color="error">사업자등록증 이미지가 없습니다.</Typography>
                )}
              </Box>
              <Box sx={{ p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>활동 지역</Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 1 }}>
                  <TextField
                    select
                    label="시/도"
                    size="small"
                    value={regionSido}
                    onChange={(e) => {
                      const nextSido = e.target.value;
                      setRegionSido(nextSido);
                      setRegionSigungu(koreaRegions.find((region) => region.sido === nextSido)?.sigungu?.[0] || "");
                    }}
                    sx={{ minWidth: 150 }}
                  >
                    {koreaRegions.map((region) => (
                      <MenuItem key={region.sido} value={region.sido}>{region.sido}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label="시/군/구"
                    size="small"
                    value={regionSigungu}
                    onChange={(e) => setRegionSigungu(e.target.value)}
                    sx={{ minWidth: 170 }}
                  >
                    {currentSigunguOptions.map((sigungu) => (
                      <MenuItem key={sigungu} value={sigungu}>{sigungu}</MenuItem>
                    ))}
                  </TextField>
                  <IconButton color="primary" onClick={addApprovalRegion} aria-label="활동 지역 추가">
                    <AddCircleOutlineIcon />
                  </IconButton>
                </Stack>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {approvalRegions.length === 0 && (
                    <Typography variant="caption" color="text.secondary">선택된 활동 지역이 없습니다.</Typography>
                  )}
                  {approvalRegions.map((region) => (
                    <Chip
                      key={`${region.sido}-${region.sigungu}`}
                      label={`${region.sido} ${region.sigungu}`}
                      onDelete={() =>
                        setApprovalRegions((prev) =>
                          prev.filter((item) => item.sido !== region.sido || item.sigungu !== region.sigungu)
                        )
                      }
                    />
                  ))}
                </Stack>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalTarget(null)}>취소</Button>
          <Button variant="contained" onClick={handleApprovalSave}>저장</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!accountStatusTarget} onClose={() => setAccountStatusTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>계정 상태 관리</DialogTitle>
        <DialogContent>
          {accountStatusTarget && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body2">
                {accountStatusTarget.name} / {accountStatusTarget.phoneNumber || "-"}
              </Typography>
              <TextField
                select
                label="계정 상태"
                value={accountStatus}
                onChange={(e) => setAccountStatus(e.target.value)}
                SelectProps={{ native: true }}
              >
                <option value="APPROVED">정상</option>
                <option value="SUSPENDED">정지</option>
                <option value="WITHDRAWN">탈퇴</option>
              </TextField>
              <Typography variant="body2" color="text.secondary">
                정지 또는 탈퇴 상태는 앱 로그인을 차단합니다. 운영 이력은 삭제하지 않고 보존됩니다.
              </Typography>
              {accountStatus === "WITHDRAWN" && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={accountStatusAnonymize}
                      onChange={(e) => setAccountStatusAnonymize(e.target.checked)}
                    />
                  }
                  label="개인정보 익명화"
                />
              )}
              {accountStatus === "WITHDRAWN" && accountStatusAnonymize && (
                <Typography variant="body2" color="error">
                  이름, 전화번호, 이메일, 주소, 사업자 정보와 토큰이 복구 불가능한 운영용 값으로 대체됩니다. 요청/검수 이력은 보존됩니다.
                </Typography>
              )}
              <TextField
                label="변경 사유"
                value={accountStatusReason}
                onChange={(e) => setAccountStatusReason(e.target.value)}
                multiline
                minRows={2}
                inputProps={{ maxLength: 500 }}
                helperText="이 사유는 관리자 처리 이력에 저장됩니다."
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAccountStatusTarget(null)}>취소</Button>
          <Button variant="contained" onClick={handleAccountStatusSave}>
            저장
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UserManagementPage;
